import {
    AfterViewInit,
    ChangeDetectorRef,
    Component,
    NgZone,
    OnDestroy,
    OnInit
} from '@angular/core';
import { Params, Router } from '@angular/router';
import { App } from '@capacitor/app';
import { SplashScreen } from '@capacitor/splash-screen';
import { StatusBar, Style } from '@capacitor/status-bar';
import { SsoFeature } from 'src/environments/features/sso.feature';

import { ModalController, Platform, ToastController } from '@ionic/angular';
import { isEmpty } from 'lodash';
import { CookieService } from 'ngx-cookie-service';
import { Subscription } from 'rxjs';
import { DiversityCheckModal } from './diversity-check/diversity-check.modal';
import { ExternalClientStylesService } from './external-client-styles/external-client-styles.service';
import { PerformanceCycleService } from './performance-cycles/performance-cycles.service';
import { AttributesService } from './processes/attributes.service';
import { SystemNotificationPaged } from './shared/classes/notification';
import { DiversityService } from './shared/diversity.service';
import { MenuPagesInterface } from './shared/interfaces/menu-pages.interface';
import { UserPreview } from './shared/interfaces/user-preview.interface';
import { NotificationModalComponent } from './shared/modals/notification-modal/notification-modal.component';
import { AuthService, LoginResponse, UserItem } from './shared/services/auth.service';
import { applyCachedExternalClientStyles, applyExternalClientStyles, ExternalClientsStylesCacheKey } from './shared/services/external-client.service';
import { LogoService } from './shared/services/logo.service';
import { NotificationService } from './shared/services/notification.service';
import { SharedService } from './shared/services/shared.service';
import { TranslationService } from './shared/services/translation.service';
import { ThemeService } from './shared/services/multi-clients/theme.service';
import { AssetService } from './shared/services/multi-clients/asset.service';
import { ConfigService } from './shared/services/multi-clients/config.service';
import { registerPlugin } from '@capacitor/core';
import { OAuth2Client } from '@byteowls/capacitor-oauth2';
import { JobFollowUpWrapperComponent } from './shared/modals/job-follow-up-wrapper/job-follow-up-wrapper.component';
import { JobFollowUpService } from './shared/modals/job-follow-up/job-follow-up.service';
import { JobStatus } from './shared/modals/job-follow-up/enums/job-status.enum';
import { REFRESHTOKEN_KEY, SSO_ERROR_KEY, SSO_SUCCESS_KEY } from 'auth/src/lib/constants';

@Component({
    selector: 'app-root',
    templateUrl: 'app.component.html', styles: [
        `
            ion-router-outlet.impersonating-bottom {
                margin-bottom: 55px;
            }
            
            #main-content {
                position: relative;
                height: 100vh;
                overflow: hidden;
            }
            
            ion-header {
                position: relative;
                z-index: 10;
            }
            
            ion-router-outlet {
                position: absolute;
                top: 56px; /* altura padrão do ion-header */
                left: 0;
                right: 0;
                bottom: 0;
                overflow: auto;
            }
            
            /* Garantir que o conteúdo interno não seja cortado */
            ion-router-outlet ion-page {
                height: 100%;
                overflow: auto;
            }
            
            ion-router-outlet ion-content {
                --height: 100%;
                height: 100%;
            }
            
            /* Ajuste para quando há toolbar de impersonation */
            /*ion-router-outlet.impersonating-bottom {
                top: 111px; /* altura do header + toolbar de impersonation
                bottom: 0;
            }*/
        `,
    ],
})
export class AppComponent implements OnDestroy, AfterViewInit, OnInit {

    public appPages: MenuPagesInterface[] = [
        {
            title: 'dashboard.viewTeamProgress',
            url: '/performance-cycles',
        }
    ];
    public selectedLanguage: string = 'pt-BR';
    private _languageSubscription: Subscription;
    public showLoader: boolean;
    private _loaderSubscription: Subscription;
    public logoUrl = '';
    public hideReports: boolean;
    public showNotificationsFeature: boolean;
    public cannotOpenProfile;
    public principlesConfigurationEnabled;

    public currentUser: UserItem;
    public styles: any;
    public notifications: SystemNotificationPaged = { lastPage: 0, unreadCount: 0, notifications: [] };
    public notificationsNotRead: number = 0;
    public jobsInProgress: number = 0;
    public userRoles: Array<string> = [];
    public passwordChangeRequired: boolean;
    public isImpersonating: boolean = false;
    public impersonatedInfo: UserPreview;
    public ssoWithRedirect: boolean;
    public overrideLogin: boolean;
    public isHrOrHasReportAccess: boolean;
    public isDirectDownloadsEnabled: boolean = false;
    public aiReviewEnabled: boolean = false;
    public aiOverviewReviewEnabled: boolean = false;

    private _userSubscription: Subscription;
    private _impersonationSubscription: Subscription;
    private _params: {} & Params;
    public showNotification: boolean = false;
    public tutorialPath: string = 'assets/documents/tutorial.pdf';
    sso: import("src/environments/features/sso.feature").SsoFeature;
    tutorialMenu: boolean;
    private _isHrOrAdmin: boolean = false;

    constructor(
        private platform: Platform,
        private _translationService: TranslationService,
        private _authService: AuthService,
        private _sharedService: SharedService,
        private _diversityService: DiversityService,
        private _router: Router,
        private modalController: ModalController,
        private zone: NgZone,
        private _cookieService: CookieService,
        protected _toastController: ToastController,
        protected _performanceCycleService: PerformanceCycleService,
        private _logoService: LogoService,
        private _notificationService: NotificationService,
        private cdr: ChangeDetectorRef,
        private _externalClientStylesService: ExternalClientStylesService,
        private _attributeService: AttributesService,
        private _assetService: AssetService,
        private _configService: ConfigService,
        private _themeService: ThemeService,
        private _jobsFollowUpService: JobFollowUpService
    ) {
        // Inicialização movida para ngOnInit
    }

    ngOnInit(): void {
        // Aguardar as configurações carregarem através do APP_INITIALIZER
        this.initializeSettings();
        this.initializeApp();
    }

    ngAfterViewInit() {
        registerPlugin('OAuth2Client', OAuth2Client);
        setTimeout(() => {
            this._loaderSubscription = this._sharedService
                .getLoaderSubject()
                .subscribe((value: boolean) => {
                    this.showLoader = value;
                    this.cdr.detectChanges();  // <<<<<< Aqui força a atualização segura
                });
        }, 500);
    }

    private initializeSettings() {
        //Feature flags
        this.sso = this._configService.settings.features.sso;
        this.hideReports = this._configService.settings.hideReports;
        this.showNotificationsFeature = this._configService.settings.features.showNotifications;
        this.tutorialMenu = this._configService.settings.features.tutorialMenu;
        this.isDirectDownloadsEnabled = this._configService.settings.features.directDownloads;
        this.aiReviewEnabled = !!this._configService.settings.features.aiReview;
        this.aiOverviewReviewEnabled = this._configService.settings.features.aiOverviewReview;
        this._translationService.initializeLanguageSettings();

        this.cannotOpenProfile = this._configService.settings.features.cannotOpenProfile;
        this.principlesConfigurationEnabled = this._configService.settings.features.principlesConfiguration;

        /**
         * Carrega tema e assets com branding dinâmico
         *
         * ThemeService.loadTheme() aplica:
         * - themeScss customizado (inline) OU
         * - arquivo CSS local (assets/themes/{clientName}.variables.css)
         *
         * AssetService.loadAssets() aplica:
         * - logo customizada (brandingData.logo)
         * - favicons customizados (brandingData.favicon16x16, favicon32x32)
         *
         * Ambos seguem prioridade: brandingData > brandingByHost > local
         */
        this._themeService.loadTheme();
        this._assetService.loadAssets();

        this.logoUrl = this._assetService.getLogoPath();

        this._logoService.logoSubject.subscribe(logo => {
            this.logoUrl = logo ? logo : this.logoUrl;
        });

        this._assetService.getTutorialPdfPath().then(path => {
            this.tutorialPath = path;
        });

        this._jobsFollowUpService.jobs$.subscribe(jobs => {
            this.jobsInProgress = jobs.filter(job => {
                const jobStatus = job.progress?.status;
                return (
                    jobStatus === JobStatus.Enqueued ||
                    jobStatus === JobStatus.Pending ||
                    jobStatus === JobStatus.Processing
                )
            }).length;
        });

    }

    private initializeDefaultSettings() {
        console.warn('⚠️  Usando configurações padrão devido a falha no carregamento');
        const clientName = 'btg'; // default
        //Feature flags padrão
        this.sso = new SsoFeature({
            enabled: false,
            logoutUrl: '',
            logoutByClient: false
        });
        this.hideReports = false;
        this.showNotificationsFeature = true;
        this.tutorialMenu = true;
        this.aiReviewEnabled = false;
        this.aiOverviewReviewEnabled = false;
        this._translationService.initializeLanguageSettings();

        this.cannotOpenProfile = false;
        this.principlesConfigurationEnabled = false;

        this._themeService.loadTheme(clientName);
        this._assetService.loadAssets(clientName);

        this._assetService.getTutorialPdfPath().then(path => {
            this.tutorialPath = path;
        });
    }

    initializeApp() {
        this.platform.ready().then(async () => {
            if (this.platform.is('capacitor')) {
                await StatusBar.setStyle({ style: Style.Default });
                await SplashScreen.hide();
            }

            let currentNavigation = this._router.getCurrentNavigation();
            if (currentNavigation && currentNavigation.extractedUrl) {
                let params = await this._router.getCurrentNavigation().extractedUrl.queryParams;
                // console.log(params);
                if (params && params.pdfExporting === 'true') {
                    console.log("isExporting");
                    localStorage.setItem('pdfExporting', 'true');
                }
            }
            this._validateAuth();
            const sLanguage = this._translationService.getStoredLanguage();
            this.selectedLanguage = sLanguage || this.selectedLanguage;

            this._languageSubscription = this._translationService.languageChanged$.subscribe(
                (language) => (this.selectedLanguage = language)
            );

            this._setMenuItems();

            this._authService.passwordChangeRequired().subscribe((required) => {
                this.passwordChangeRequired = required;
            });

            this.impersonatedInfo = this._authService.getImpersonatedUser();
            this.isImpersonating = this.impersonatedInfo && true;

            this._impersonationSubscription = this._authService.personificationChanged$.subscribe(
                (isImpersonating) => {
                    this.isImpersonating = isImpersonating;
                    this.impersonatedInfo = this._authService.getImpersonatedUser();
                });

            if (this.platform.is('capacitor')) {
                App.addListener('appUrlOpen', (data: any) => {
                    this.zone.run(() => {
                        if (data.url.includes('/login')) return;
                        const slug = data.url.split('.com').pop();
                        if (slug) {
                            this._router.navigateByUrl(slug);
                        }
                    });
                });
            }
        });
    }

    private _processTokenAuthentication(TOKEN_KEY: string): boolean {
        let authenticationInfoToParse = this._cookieService.get(TOKEN_KEY);
        // console.log('🔐 DEBUG: TOKEN_KEY:', TOKEN_KEY, 'Value:', authenticationInfoToParse);
        // console.log('_params: ', this._params);
        if (this._params?.[TOKEN_KEY] || authenticationInfoToParse) {
            // console.log('_params: ', this._params?.[TOKEN_KEY]);
            // console.log('authenticationInfoToParse: ', authenticationInfoToParse);
            if (this._params?.[TOKEN_KEY] === 'false') {
                // console.log('_params: ', this._params);
                let correctUrl = this._params['correctUrl'];
                if (correctUrl) {
                    let message = this._translationService.getTranslation('login.wrongDNS');
                    this.notify(message + " [https://" + correctUrl + "]", 'danger', 5000);
                    this._authService.logout();
                    return false;
                }
            }
            if (!authenticationInfoToParse) {
                authenticationInfoToParse = JSON.stringify(this._params ?? {});
            }

            const parsed = JSON.parse(authenticationInfoToParse);
            if (!parsed.tokenResource) {
                authenticationInfoToParse = JSON.stringify({ tokenResource: parsed });
            }
            // console.log(parsed)
            const tokenResource: {
                firstAccess,
                accessToken,
                username,
                userId,
                name,
                roles
            } = (JSON.parse(authenticationInfoToParse) || {})['tokenResource'];
            // console.log(tokenResource);
            if (!tokenResource) {
                this._toastController.create({ message: 'login.noUserInfo', color: 'danger' });
                return false;
            }
            this._authService.ssoLogin(tokenResource);
            this._cookieService.delete(TOKEN_KEY, '/', undefined);

            if (this._cookieService.check(TOKEN_KEY)) {
                console.warn('Cookie SSO não foi removido completamente, forçando limpeza...');
                this._cookieService.delete(TOKEN_KEY, '/', undefined);
                this._cookieService.delete(TOKEN_KEY);
                // Se ainda existir, deleta todos os cookies do domínio (medida extrema)
                if (this._cookieService.check(TOKEN_KEY)) {
                    this._cookieService.deleteAll('/');
                }
            }

            return true;
        }
        return false;
    }

    private _validateAuth() {
        const params = new URLSearchParams(window.location.search);

        if (this.platform.is('capacitor')) {
            let hasToken = this._authService.hasToken();
            console.log('📱 DEBUG: Capacitor - hasToken:', hasToken);

            if (hasToken) {
                if (this._params && this._params.FirstAccess && this._params.FirstAccess === 'True') {
                    this._router.navigate(['first-access']);
                } else if (window.location.pathname === '/' || window.location.pathname.includes('login')) {
                    this._router.navigate(['dashboard']);
                }
            } else {
                this._router.navigate(['login']);
            }
            return;
        }


        const SSOErrorMessage = this._cookieService.get(SSO_ERROR_KEY);
        const errorInSsoResponse = this._params && this._params[SSO_ERROR_KEY] || SSOErrorMessage;

        this._params = this._urlSearchParamsToObject(params);
        this.ssoWithRedirect = this.sso.redirect.enabled;

        let hasToken = this._authService.hasToken();

        this.overrideLogin = this._params['overrideLogin'] === '1';
        // console.log('sso.enabled: ', this.sso.enabled);
        if (this.sso.enabled) {
            // console.log('cookies:', this._cookieService.getAll())
            if (this._processTokenAuthentication(SSO_SUCCESS_KEY)) {
                hasToken = this._authService.hasToken();
            }
        }

        // Processar refresh_token se existir (após redirect do refresh)
        if (this._processTokenAuthentication(REFRESHTOKEN_KEY)) {
            hasToken = this._authService.hasToken();
        }
        if (hasToken) {
            if (this._params.FirstAccess && this._params.FirstAccess === 'True') {
                this._router.navigate(['first-access']);
            } else if (window.location.pathname === '/' || window.location.pathname.includes('login')) {
                this._router.navigate(['dashboard']);
            }
        } else if (errorInSsoResponse) {
            // Sempre usar a chave genérica de erro SSO, ignorando o valor do cookie
            const translatedError = this._translationService.getTranslation(
                'login.sso.authenticationFailed'
            );

            this._toastController.create({
                message: translatedError,
                color: 'danger',
                duration: 5000
            }).then(toast => {
                toast.present();
                // Deletar cookie DEPOIS de apresentar o toast
                if (SSOErrorMessage) {
                    this._cookieService.delete(SSO_ERROR_KEY, '/', undefined);
                }
            });
        } else if (!hasToken) {
            if (this.ssoWithRedirect && !this.overrideLogin) {
                // Redirecionar para o endpoint SSO
                window.location.href = this.sso.redirect.endpoint;
            } else {
                // Redirecionar para a tela de login
                const params = this.overrideLogin ? { queryParams: { overrideLogin: 1 } } : {};
                const path = window.location.pathname.replace('/', '');
                const isRouteValid = !isEmpty(path);
                this._router.navigate([isRouteValid ? path : '/login'], params);
            }
        }
    }

    private _setMenuItems() {
        // console.log('📋 DEBUG: _setMenuItems iniciado');
        this._authService.isLoggedIn().subscribe((isLoggedIn) => {
            // console.log('🔐 DEBUG: isLoggedIn:', isLoggedIn);
            if (isLoggedIn) {
                this._authService.getUserData().subscribe((response) => {
                    // console.log('📊 DEBUG: getUserData response:', response);
                    this.currentUser = response.data;

                    // Verificar se o token precisa ser atualizado com novas roles
                    if (this.currentUser?.tokenNeedsRefresh) {
                        // Verifica se já tentou fazer refresh nesta sessão para evitar loop
                        const alreadyRefreshed = sessionStorage.getItem('token_refreshed');
                        if (alreadyRefreshed) {
                            console.warn('⚠️ Token already refreshed in this session, skipping to avoid loop');
                            sessionStorage.removeItem('token_refreshed');
                            this._continueMenuSetup();
                            return;
                        }

                        console.log('🔄 Token needs refresh - updating token with new roles');
                        this._authService.refreshUserToken(this.currentUser.id).subscribe(
                            (refreshResponse) => {
                                console.log('Refresh token response:', refreshResponse);
                                if (refreshResponse.success) {
                                    // Cookie já foi setado via withCredentials
                                    const cookieKey = 'refresh_token';
                                    const cookieValue = this._cookieService.get(cookieKey);
                                    console.log('cookieValue:', cookieValue);
                                    if (cookieValue) {
                                        try {
                                            const parsed = JSON.parse(cookieValue);
                                            console.log('parsed:', parsed);
                                            const tokenResource = parsed.tokenResource;
                                            if (tokenResource) {
                                                console.log('tokenResource:', tokenResource);
                                                // Atualiza apenas token e roles, mantém outros dados
                                                this._authService.updateTokenAndRoles(tokenResource);
                                                console.log('✅ Token and roles updated in AuthService');
                                                this._cookieService.delete(cookieKey, '/', undefined);
                                                sessionStorage.setItem('token_refreshed', 'true');
                                                console.log('✅ Token and roles refreshed successfully');

                                                // Força recarga do menu com novas roles
                                                this._setMenuItems();
                                            }
                                        } catch (error) {
                                            console.error('❌ Error parsing refresh token cookie:', error);
                                        }
                                    }
                                }
                                this._continueMenuSetup();
                            },
                            (error) => {
                                console.error('❌ Error refreshing token:', error);
                                this._continueMenuSetup();
                            }
                        );
                    } else {
                        // Limpa flag de refresh se não precisa mais
                        sessionStorage.removeItem('token_refreshed');
                        this._continueMenuSetup();
                    }
                });
            } else {
                this.currentUser = null;
                this.userRoles = [];
                this.appPages = [
                    {
                        title: 'dashboard.viewTeamProgress',
                        url: '/performance-cycles',
                    }
                ];
            }
        });
    }

    private _continueMenuSetup() {
        // Reset do menu para evitar duplicações em múltiplas chamadas
        this.appPages = [{
            title: 'dashboard.viewTeamProgress',
            url: '/performance-cycles',
        }];

        // Debug das condições do menu
        // console.log('🔍 DEBUG: currentUser:', this.currentUser);
        // console.log('🔍 DEBUG: currentUser.manager:', this.currentUser?.manager);
        // console.log('🔍 DEBUG: isHrOrHasReportAccess:', this.isHrOrHasReportAccess);
        // console.log('🔍 DEBUG: passwordChangeRequired:', this.passwordChangeRequired);
        // console.log('🔍 DEBUG: isAdmin():', this.isAdmin());

        this.initExternalClientStyles();
        this.userRoles = this._authService.getUserRoles();
        this.IsHrOrHasReportAccess();
        this._hasHrOrAdmin();

        // Forçar detecção de mudanças
        this.cdr.detectChanges();

        let parameters: MenuPagesInterface = {
            title: 'generic.parameters',
            //url: '',
            children: []
        };

        if (this.userRoles.includes('Administrator')) {
            this.appPages.splice(1, 0, {
                title: 'generic.performanceProcesses',
                url: '/processes',
            });
        }

        if (this.userRoles.includes('UserManagement')) {
            this.appPages.splice(1, 0, {
                title: 'Processo de Sincronia',
                url: '/usuarios/processo-de-sincronia',
            });
        }

        if (this.userRoles.includes('UserManagement')) {
            this.appPages.push({
                title: 'generic.userManagement',
                url: '/users/admin',
            });
        }

        if (this.userRoles.includes('VisibilityManagement')) {
            this.appPages.push({
                title: 'generic.visibilityManagement',
                url: '/users/visibility',
            });
        }

        if (
            this.userRoles.includes('Administrator') ||
            this.userRoles.includes('OKRAdmin')
        ) {
            this.appPages.push({
                title: 'generic.OKRAdmin',
                url: '/okr',
            });
        }

        if (this.userRoles.includes('Administrator') ||
            this.userRoles.includes('HR')) {
            this.appPages.push({
                title: 'Feedbacks',
                url: '/feedback',
            });
        }

        if (this.userRoles.includes('Administrator') ||
            this.userRoles.includes('HR')) {
            this.appPages.push({
                title: 'ExternalEvaluation',
                url: '/external-evaluations',
            });
        }

        if (!this.hideReports ||
            this.userRoles.includes('Administrator') ||
            this.userRoles.includes('HR') ||
            this.userRoles.includes('ReportAccess')) {
            this.appPages.push({
                title: 'generic.reports',
                url: '/reports',
            });
        }

                    if (this.userRoles.includes('Administrator')) {
                        parameters.children.push({
                            title: this._translationService.getTranslation('partnerConfig.title'),
                            url: '/partner-config',
                        });
                    }
                    
                    if (this.userRoles.includes('AI-Review') && this.aiReviewEnabled) {
                        parameters.children.push(
                            {
                                title: 'generic.reviews',
                                url: '/parameters/reviews-config'
                            }
                        );
                    }
                    if (this.userRoles.includes('AI-Review') && this.aiOverviewReviewEnabled) {
                        parameters.children.push(
                            {
                                title: 'generic.aiconfig',
                                url: '/parameters/ai-config'
                            }
                        );
                    }

        if (this.principlesConfigurationEnabled && this._isHrOrAdmin) {
            parameters.children.push(
                {
                    title: 'generic.principlesConfiguration',
                    url: '/parameters/principles-config'
                }
            );
        }

        if (this._isHrOrAdmin) {
            parameters.children.push(
                {
                    title: 'generic.videosConfiguration',
                    url: '/parameters/videos-config'
                }
            );
        }

        if (parameters && parameters.children && parameters.children.length > 0) {
            this.appPages.push(parameters);
        }

        this._diversityService
            .getLatestDiversityCheck()
            .subscribe((latestDiversityCheck) => {
                if (latestDiversityCheck.data.needsToAccept) {
                    this._createDiversityModal().then((diversityModal) => {
                        diversityModal.onDidDismiss().then((dismissEvent) => {
                            if (!dismissEvent.data) this._authService.logout();
                        });
                        diversityModal.present();
                    });
                }
            });
        this.getNotifications();
    }

    private _urlSearchParamsToObject(searchParams) {
        const object = {};
        for (const [key, value] of searchParams.entries()) {
            if (!object.hasOwnProperty(key)) {
                object[key] = value;
            } else {
                // If the key already exists, convert the value to an array
                // and push the new value to the array
                if (!Array.isArray(object[key])) {
                    object[key] = [object[key]];
                }
                object[key].push(value);
            }
        }
        return object;
    }

    public IsHrOrHasReportAccess() {
        this.isHrOrHasReportAccess = this.userRoles && (this.userRoles.includes('HR') || this.userRoles.includes('ReportAccess'));
    }

    private _hasHrOrAdmin() {
        this._isHrOrAdmin = this.userRoles && (this.userRoles.includes('HR') || this.userRoles.includes('ReportAccess'));
    }

    public isAdmin() {
        if (this.userRoles.includes('Administrator') || this.userRoles.includes('OKRAdmin')) {
            return true;
        }
        return false;
    }

    private async _createDiversityModal() {
        return await this.modalController.create({
            component: DiversityCheckModal,
            cssClass: 'diversity-check-modal',
            componentProps: { accepted: false },
            backdropDismiss: false,
        });
    }

    public goToDashboard(): void {
        if (!this.passwordChangeRequired) this._router.navigate(['dashboard']);
    }

    openProfile() {
        if (!this.cannotOpenProfile) {
            this._router.navigate(['profile']);
        }
    }

    public leaveImpersonation(): void {
        this._performanceCycleService.leaveImpersonation().subscribe(res => {
            if (res.success) {
                let response = res.data;
                this._authService.clearImpersonationInfo();
                let actualAuth = this._authService.getLoggedUser();
                let newAuth: LoginResponse = {
                    'Name': undefined,
                    'UserId': undefined,
                    'completed_registration': undefined,
                    'email_verified': undefined,
                    'first_access': undefined,
                    'imageUrl': undefined,
                    'refresh_token': undefined,
                    'role': undefined,
                    'roles': actualAuth.roles,
                    'sso': undefined,
                    'access_token': response.tokenResource.accessToken,
                    'username': actualAuth.username,
                    'user_id': actualAuth.user_id,
                    'name': actualAuth.name,
                    'expires_in': undefined,
                    'country': undefined
                };

                this._authService.setLoggedUser(newAuth).then(_ => {
                    this._router.navigate(['performance-cycles']);
                    setTimeout(() => {
                        window.location.reload();
                    });
                });
                this.getNotifications();
                this.initExternalClientStyles();

            }
        }, error => {
            throw new Error(error.message);
        });
    }

    public getUserName() {



        let impersonateUser: UserItem | null;
        impersonateUser = localStorage.getItem('ImpersonatedUser') ? JSON.parse(localStorage.getItem('ImpersonatedUser')) : null;
        return impersonateUser && impersonateUser.username
            ? impersonateUser.username
            : (this.currentUser && this.currentUser.username
                ? this.currentUser.username
                : '');
    }

    public getUserPicture() {
        var impersonateUser: UserItem | null;
        impersonateUser = localStorage.getItem('ImpersonatedUser') ? JSON.parse(localStorage.getItem('ImpersonatedUser')) : null;
        return impersonateUser && impersonateUser.imageUrl
            ? impersonateUser.imageUrl
            : (this.currentUser && this.currentUser.imageUrl
                ? this.currentUser.imageUrl
                : 'assets/imgs/user-image-placeholder.png');
    }

    public async changeLanguage(language: string): Promise<void> {
        await this._translationService.changeSelectedLanguage(language);
    }

    public logOut(): void {
        this._authService.logout();
    }

    public getNotifications() {
        if (!this.showNotificationsFeature) {
            console.log('Notifications feature is disabled');
            return;
        }
        this._notificationService.getNotifications().subscribe((response) => {
            this.notifications = response.data;
            this.notificationsNotRead = this.notifications.unreadCount;
        });
    }

    public initExternalClientStyles() {
        applyExternalClientStyles(
            this._attributeService,
            this._externalClientStylesService,
            this._logoService,
            this._authService,
            this.currentUser ? this.currentUser.id : null
        );
    }

    public initCachedExternalClientStyles() {
        this.impersonatedInfo = this._authService.getImpersonatedUser();
        applyCachedExternalClientStyles(
            this._logoService,
            this._authService,
            this.impersonatedInfo?.userId ?? this.currentUser?.id
        );
    }

    public async openNotificationModal() {
        const modalRef = await this.modalController.create({
            component: NotificationModalComponent,
            // cssClass: 'diversity-check-modal',
            componentProps: { notifications: this.notifications },
            // backdropDismiss: false,
        });

        await modalRef.present();
        modalRef.onDidDismiss().then((value) => {
            if (value.data.updatedMessage) {
                this.getNotifications();
            }
        })

    }

    public async openJobFollowUpModal() {
        const modalRef = await this.modalController.create({
            component: JobFollowUpWrapperComponent,
            cssClass: 'extra-bigger-modal',
            componentProps: { filterByPage: false },
        });

        await modalRef.present();
    }

    private async notify(message: string, color: string = 'dark', duration?: number) {
        const toast = await this._toastController.create({
            message: message,
            position: 'top',
            color: color,
            duration: duration || 3000
        });
        await toast.present();
    }

    ngOnDestroy() {
        this._languageSubscription.unsubscribe();
        this._userSubscription.unsubscribe();
        this._impersonationSubscription.unsubscribe();
        this._loaderSubscription.unsubscribe();
    }
}
